/* ─────────────────────────────────────────────────────────────
   LUXE Bundles — light JS for:
     1. Carousel prev/next buttons (scroll-snap-based)
     2. Category chip filtering
     3. Inline ATC handler (cta_mode = 'atc')

   No framework dependencies. Mounts on DOMContentLoaded + survives
   Shopify customizer section reloads via shopify:section:load.
   ───────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  function $$ (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function initSection (section) {
    if (!section || section.__luxeBundlesInit) return;
    section.__luxeBundlesInit = true;

    initCarousel(section);
    initFilters(section);
    initATC(section);
  }

  /* ── Carousel ─────────────────────────────────────────── */
  function initCarousel (section) {
    if (section.dataset.layout !== 'carousel') return;
    var list = section.querySelector('.luxe-bundles__list--carousel');
    var prev = section.querySelector('.luxe-bundles__nav-btn--prev');
    var next = section.querySelector('.luxe-bundles__nav-btn--next');
    if (!list || !prev || !next) return;

    function step () {
      var firstItem = list.querySelector('.luxe-bundles__item');
      if (!firstItem) return list.clientWidth * 0.8;
      var styles = getComputedStyle(list);
      var gap = parseFloat(styles.gap || styles.columnGap || '0');
      return firstItem.getBoundingClientRect().width + gap;
    }

    function updateButtons () {
      var maxScroll = list.scrollWidth - list.clientWidth - 1;
      prev.disabled = list.scrollLeft <= 1;
      next.disabled = list.scrollLeft >= maxScroll;
    }

    prev.addEventListener('click', function () {
      list.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      list.scrollBy({ left: step(), behavior: 'smooth' });
    });
    list.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    updateButtons();
  }

  /* ── Category filter chips ────────────────────────────── */
  function initFilters (section) {
    var chips = $$('.luxe-bundles__chip', section);
    if (!chips.length) return;
    var items = $$('.luxe-bundles__item', section);

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var target = (chip.dataset.category || '__all__');

        chips.forEach(function (c) {
          var active = c === chip;
          c.classList.toggle('luxe-bundles__chip--active', active);
          c.setAttribute('aria-selected', active);
        });

        items.forEach(function (item) {
          var cat = item.dataset.category || '';
          var show = (target === '__all__') || (cat.toLowerCase() === target.toLowerCase());
          item.classList.toggle('is-filtered-out', !show);
        });
      });
    });
  }

  /* ── Inline Add-to-cart ───────────────────────────────── */
  /* Mirrors the theme's native product-form submit flow:
       1. POSTs to /cart/add.js with `sections` so the response includes
          the rendered cart-drawer HTML
       2. Calls miniCart.renderContents(response) which updates the drawer
          DOM AND opens it (same path as native PDP ATC)
     Falls back to redirect-to-cart if the mini-cart element isn't on the page.

     Supports two button shapes:
       1. data-atc-variant="123"     → single-variant ATC (variant bundles)
       2. data-atc-items='[{"id":..,"quantity":..}, ...]'
                                     → multi-variant ATC (cart bundles) */
  function initATC (section) {
    var buttons = $$('button[data-atc-variant], button[data-atc-items]', section);
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var items = null;

        if (btn.hasAttribute('data-atc-items')) {
          try {
            items = JSON.parse(btn.getAttribute('data-atc-items'));
          } catch (e) {
            console.error('[luxe-bundles] bad data-atc-items JSON', e);
            return;
          }
        } else {
          var variantId = btn.getAttribute('data-atc-variant');
          if (!variantId) return;
          items = [{ id: parseInt(variantId, 10), quantity: 1 }];
        }
        if (!items || !items.length) return;

        var originalLabel = btn.innerHTML;
        btn.classList.add('is-loading');
        btn.setAttribute('disabled', 'disabled');

        /* Find the theme's mini-cart so we can include the right `sections`
           in the POST and trigger renderContents() afterwards. */
        var miniCart = document.querySelector('mini-cart');

        /* Use FormData so we can attach `sections` like the theme does.
           The /cart/add.js endpoint accepts items[][id]/items[][quantity]
           in form-encoded form. */
        var formData = new FormData();
        items.forEach(function (item, i) {
          formData.append('items[' + i + '][id]', item.id);
          formData.append('items[' + i + '][quantity]', item.quantity || 1);
        });

        if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
          var sectionIds = miniCart.getSectionsToRender().map(function (s) { return s.section; });
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
          if (typeof miniCart.setActiveElement === 'function') {
            miniCart.setActiveElement(btn);
          }
        }

        fetch((window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        })
        .then(function (res) {
          return res.json().then(function (parsed) {
            if (!res.ok || parsed.status) {
              var msg = parsed.description || parsed.message || ('HTTP ' + res.status);
              throw new Error(msg);
            }
            return parsed;
          });
        })
        .then(function (response) {
          btn.classList.remove('is-loading');
          btn.classList.add('is-success');
          btn.innerHTML = 'Added ✓';

          /* Mirror the theme's publish() pattern so any other listeners
             (badges, micro-carts, analytics) update too. */
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'luxe-bundles',
                productVariantId: items[0].id,
                cartData: response
              });
            } catch (_) { /* swallow */ }
          }

          /* Render + open the drawer the same way product-form does. */
          if (miniCart && typeof miniCart.renderContents === 'function') {
            miniCart.renderContents(response);
            if (miniCart.classList && miniCart.classList.contains('is-empty')) {
              miniCart.classList.remove('is-empty');
            }
          } else {
            /* No drawer present — fall back to redirect-to-cart, same as theme. */
            window.location = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
            return;
          }

          setTimeout(function () {
            btn.classList.remove('is-success');
            btn.innerHTML = originalLabel;
            btn.removeAttribute('disabled');
          }, 1800);
        })
        .catch(function (err) {
          console.error('[luxe-bundles] ATC error', err);
          btn.classList.remove('is-loading');
          btn.innerHTML = 'Try again';
          setTimeout(function () {
            btn.innerHTML = originalLabel;
            btn.removeAttribute('disabled');
          }, 1500);
        });
      });
    });
  }

  /* ── Mount ────────────────────────────────────────────── */
  function bootAll () {
    $$('.luxe-bundles').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  // Survive customizer reloads
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('.luxe-bundles') : null;
    if (root) initSection(root);
  });
})();
